/**
 * The machinery every overlay in the console shares: a portal out of the
 * layout, a focus trap, Escape handling that only reaches the topmost overlay,
 * and inerting everything behind.
 *
 * ## Why this is hand-rolled rather than `<dialog showModal()>`
 *
 * The native element is the better primitive in a browser and it is not close.
 * It puts the overlay in the top layer, so no ancestor's `overflow: hidden` can
 * clip it and no z-index can beat it; it traps focus in the engine, including
 * the cases a JavaScript trap cannot see, such as focus leaving for the address
 * bar and coming back; and it makes the rest of the page inert for free.
 *
 * It was rejected for one specific, checkable reason: jsdom 30, which is what
 * `vitest` runs this project's tests in, ships `HTMLDialogElement` with exactly
 * one member on its prototype, `open`. `showModal`, `show` and `close` do not
 * exist. Every test that opens a modal throws `showModal is not a function`,
 * and the conventions here require tests that query by role and accessible
 * name, which means opening the thing.
 *
 * The obvious repair is a shim in `src/test/setup.ts`. That was rejected too,
 * and this is the part that decided it: a shim makes the trap, the Escape
 * behaviour and the inertness under test the shim's behaviour, not the
 * browser's. The tests would then assert against a fiction, and the assertions
 * would pass whatever the real dialog did. Testing a trap we wrote is honest;
 * testing a stub of a trap we did not write is theatre.
 *
 * So the top layer is replaced by a portal to `document.body` plus the
 * `--z-modal` token, and inertness is replaced by `useBackgroundInert`, which
 * is the part hand-rolled overlays usually forget. Real browser behaviour is
 * verified by the Playwright suites, where the real engine is present.
 */

import { useEffect, useRef, useState } from 'react'
import type { PointerEvent, RefObject } from 'react'

/**
 * Everything the platform will move focus to with Tab.
 *
 * `[tabindex]:not([tabindex="-1"])` is last so that a panel given
 * `tabIndex={-1}` purely to receive initial focus never becomes a tab stop of
 * its own.
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * The tabbable descendants of `root`, in document order.
 *
 * Deliberately not filtered by visibility. The usual filter is
 * `element.offsetParent !== null`, and jsdom performs no layout, so under test
 * every element reports `offsetParent === null` and that filter returns an
 * empty list. The trap then silently becomes a no-op in precisely the
 * environment where it is being verified, and the test still passes. `inert`
 * and `aria-hidden` are checked instead, because both are attributes and both
 * mean the same thing in jsdom as in Chrome.
 */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('inert') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.closest('[inert],[hidden]') === null,
  )
}

/**
 * A host element under `document.body` for the overlay to render into.
 *
 * Portalling out of the layout is what stops the shell's grid, the SplitView's
 * scroll containers and a Panel's rounded overflow from clipping a modal. The
 * element is created once per overlay and only attached while it is open, so a
 * page holding five closed drawers leaves five stray divs out of the document
 * instead of in it.
 */
export function useOverlayPortal(open: boolean): HTMLElement | null {
  const [host] = useState(() => {
    const element = document.createElement('div')
    // Read by useBackgroundInert, which must not inert another overlay.
    element.setAttribute('data-overlay-host', '')
    return element
  })

  useEffect(() => {
    if (!open) return
    document.body.appendChild(host)
    return () => {
      host.remove()
    }
  }, [host, open])

  return open ? host : null
}

/*
 * Background inertness, reference counted.
 *
 * Counting matters: with two overlays open, the inner one closing must not
 * un-inert the page while the outer one is still up. Without the count the
 * background behind a confirm dialog raised from a drawer becomes clickable the
 * moment the confirm closes, which is the exact moment the person is about to
 * click.
 */
let inertDepth = 0
let inerted: HTMLElement[] = []

function applyBackgroundInert() {
  inertDepth += 1
  if (inertDepth > 1) return
  inerted = Array.from(document.body.children).filter(
    (element): element is HTMLElement =>
      element instanceof HTMLElement &&
      !element.hasAttribute('data-overlay-host') &&
      !element.hasAttribute('inert'),
  )
  for (const element of inerted) element.setAttribute('inert', '')
}

function releaseBackgroundInert() {
  inertDepth -= 1
  if (inertDepth > 0) return
  inertDepth = 0
  for (const element of inerted) element.removeAttribute('inert')
  inerted = []
}

/**
 * Makes everything outside the open overlays unreachable: not by pointer, not
 * by Tab, and not by a screen reader's virtual cursor.
 *
 * A focus trap alone covers only the third of those. Someone reading with a
 * screen reader can still swipe past the trap into the table behind and act on
 * a row that the dialog was asking them about, and a mouse can still click it.
 */
export function useBackgroundInert(open: boolean) {
  useEffect(() => {
    if (!open) return
    applyBackgroundInert()
    return releaseBackgroundInert
  }, [open])
}

/*
 * Escape, routed to the topmost overlay only.
 *
 * Every open overlay listening on `document` means one Escape closes the
 * confirm dialog and the drawer that raised it together, and the person loses
 * the form they had half filled in behind it. A stack with a single listener
 * gives Escape the meaning it has everywhere else: dismiss the thing on top.
 */
type CloseHandler = () => void
const escapeStack: CloseHandler[] = []

function onDocumentEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  const top = escapeStack[escapeStack.length - 1]
  if (!top) return
  event.preventDefault()
  event.stopPropagation()
  top()
}

export function useEscapeToClose(open: boolean, onClose: () => void) {
  const latest = useRef(onClose)

  // Written in an effect rather than during render, so a re-render that React
  // throws away cannot leave the stack pointing at a callback that was never
  // committed.
  useEffect(() => {
    latest.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const handler = () => latest.current()
    escapeStack.push(handler)
    if (escapeStack.length === 1) document.addEventListener('keydown', onDocumentEscape, true)

    return () => {
      const index = escapeStack.lastIndexOf(handler)
      if (index >= 0) escapeStack.splice(index, 1)
      if (escapeStack.length === 0) document.removeEventListener('keydown', onDocumentEscape, true)
    }
  }, [open])
}

/**
 * Keeps Tab inside `panelRef` while open, and puts focus back where it came
 * from on close.
 *
 * Initial focus goes to the panel itself rather than to its first control. A
 * screen reader announces the element that receives focus, so focusing the
 * first button announces "Cancel, button" and the person never hears the title
 * or the consequence the dialog was opened to state. Focusing the panel
 * announces the dialog's name and its description first, and Tab then reaches
 * the controls in order. Pass `initialFocusRef` for the rare case that wants
 * something else, such as a search field in a command palette.
 *
 * Restoration checks `isConnected` because the trigger is frequently a button
 * in a row that the action just removed. When that element is gone, focus falls
 * to `<body>` and a keyboard user is silently returned to the top of the
 * document; the shell's `<main>` is the nearest honest landing place.
 */
export function useFocusTrap(
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  initialFocusRef?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return

    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const initial = initialFocusRef?.current ?? panel
    initial.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const items = focusableWithin(panel)
      const first = items[0]
      const last = items[items.length - 1]

      if (!first || !last) {
        // Nothing to move to. Holding focus on the panel is better than letting
        // Tab escape into an inert page and leave the caret nowhere.
        event.preventDefault()
        panel.focus()
        return
      }

      const active = document.activeElement
      if (!(active instanceof HTMLElement) || !panel.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault()
        last.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      if (restoreTo?.isConnected) {
        restoreTo.focus()
        return
      }
      const main = document.querySelector('main')
      if (main instanceof HTMLElement) main.focus()
    }
  }, [open, panelRef, initialFocusRef])
}

/**
 * Closes when a pointer goes down on the scrim itself.
 *
 * `pointerdown` and not `click`. A click fires on the nearest common ancestor
 * of press and release, so selecting a sentence inside the panel and releasing
 * the mouse a few pixels outside it registers as a click on the scrim and
 * closes the dialog, taking the half-written note with it. A pointerdown that
 * starts on the panel never reaches the scrim at all.
 */
export function scrimPointerDown(onClose: () => void) {
  return (event: PointerEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return
    onClose()
  }
}
